import React, { useEffect, useMemo, useState } from 'react';
import { Container, Typography, CircularProgress, Box, Theme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { useSearchParams } from 'react-router-dom';
import config from '../../config';
import { getSupportedLanguageId } from '../../i18n/helper';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        container: {
            paddingTop: theme.spacing(10),
            paddingBottom: theme.spacing(4),
        },
    }),
);

const defaultDocument = 'getting-started.md';
const externalHelpLinkPrefixes = ['#', 'http://', 'https://', 'mailto:', 'tel:'];

function normalizeDocPath(path?: string | null): string | null {
    if (!path) {
        return null;
    }

    const [pathWithoutSearch] = path.split('?');
    const [pathWithoutHash] = pathWithoutSearch.split('#');
    const pathFragments = pathWithoutHash.split('/');
    const normalizedFragments: string[] = [];

    for (const fragment of pathFragments) {
        if (!fragment || fragment === '.') {
            continue;
        }

        if (fragment === '..') {
            if (normalizedFragments.length === 0) {
                return null;
            }
            normalizedFragments.pop();
            continue;
        }

        normalizedFragments.push(fragment);
    }

    return normalizedFragments.join('/');
}

function extractDirectory(path: string): string {
    return path.includes('/')
        ? path.slice(0, path.lastIndexOf('/') + 1)
        : '';
}

export default function HelpPage() {
    const classes = useStyles();
    const { t, i18n } = useTranslation('common');
    const [searchParams, setSearchParams] = useSearchParams();
    const [markdownContent, setMarkdownContent] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const currentDocument = useMemo(
        () => normalizeDocPath(searchParams.get('doc')) ?? defaultDocument,
        [searchParams],
    );

    useEffect(() => {
        const language = getSupportedLanguageId(i18n.language);
        const url = `${config.docsBaseUrl}/${language}/${currentDocument}`;

        setLoading(true);
        setError(false);

        fetch(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch: ${response.status}`);
                }
                return response.text();
            })
            .then(text => {
                setMarkdownContent(text);
                setLoading(false);
            })
            .catch(() => {
                setError(true);
                setLoading(false);
            });
    }, [currentDocument, i18n.language]);

    return (
        <main className={classes.content}>
            <Container maxWidth="md" className={classes.container}>
                {loading && (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <CircularProgress />
                    </Box>
                )}
                {error && (
                    <Typography color="error">{t('help.loadError')}</Typography>
                )}
                {!loading && !error && (
                    <Markdown
                        skipHtml
                        components={{
                            a: ({ href, children, ...props }) => (
                                <a
                                    href={href}
                                    {...props}
                                    onClick={(event) => {
                                        if (!href || externalHelpLinkPrefixes.some((prefix) => href.startsWith(prefix))) {
                                            return;
                                        }

                                        const normalizedCurrentPath = normalizeDocPath(currentDocument) ?? defaultDocument;
                                        const currentDirectory = extractDirectory(normalizedCurrentPath);
                                        const resolvedPath = normalizeDocPath(`${currentDirectory}${href}`);

                                        if (!resolvedPath) {
                                            return;
                                        }

                                        event.preventDefault();
                                        setSearchParams(resolvedPath === defaultDocument ? {} : { doc: resolvedPath });
                                    }}
                                >
                                    {children}
                                </a>
                            ),
                        }}
                    >
                        {markdownContent}
                    </Markdown>
                )}
            </Container>
        </main>
    );
}
